import React, {
	Fragment,
	useState,
	useCallback,
	useMemo,
	useContext,
	createContext,
	useEffect,
} from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { useCoordination } from '@atlassiansox/engagekit-ts';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import {
	SpotlightManager,
	SpotlightTarget,
	SpotlightPulse,
	SpotlightTransition,
	Spotlight,
} from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';
import { useCoordinationClient } from '@confluence/engagement-provider';
import { fg } from '@confluence/feature-gating';

// Analytics Migration changeboarding components
// This experience consists of 2 connected steps; first the pulsing button on analytics byline.
// Then the analytics dialog, where we'll see the second pulse + spotlight.
// The three exported components are controlled by a FG which will turn off the whole experience for everyone.
//
// FG: confluence_fe_analytics_migration_changeboarding
// EP message: https://engage-api.prod.atl-paas.net/#/messages/cc-analytics-migration-changeboarding
// Audience: https://data-portal.internal.atlassian.com/targeting/registry/2f9fabd0-5382-40f4-afd3-06a75a33ffee
const ENGAGEMENT_ID = 'cc-analytics-migration-changeboarding';
const LOCALSTORAGE_KEY = localStorageKeys.ANALYTICS_MIGRATION_CHANGEBOARDING_MESSAGE; // analytics-migration-changeboarding-message
const TARGET_ON_DOM = 'analytics-migration-changeboarding-spotlight';
const GASV3_EVENT_NAME = 'analyticsMigrationChangeboarding';
const MIN_VIEWERS_COUNT_REQUIREMENT = 10;

const i18n = defineMessages({
	title: {
		id: 'confluence-analytics.changeboarding.title',
		defaultMessage: 'Enhanced page analytics',
		description: 'Title of the spotlight',
	},
	content: {
		id: 'confluence-analytics.changeboarding.content',
		defaultMessage:
			'The page analytics experience has been redesigned for greater simplicity and insight! Effortlessly track your page’s performance and view stakeholder engagement at a glance, all in a fresh, intuitive layout.',
		description: 'The content of the spotlight which introduces the redesigned analytics page',
	},
	ctaButton: {
		id: 'confluence-analytics.changeboarding.ctaButton',
		defaultMessage: 'Check it out',
		description:
			'The text of the button in the spotlight which sends the user to the analytics page',
	},
	cancelButton: {
		id: 'confluence-analytics.changeboarding.cancelButton',
		defaultMessage: 'Dismiss',
		description:
			'The text of the button in the spotlight which closes the modal and dismisses the experience',
	},
});

interface ChangeboardingContextValue {
	// Based on isDismissed (local state), localStorage (shared state) + EP's coordination and FG value (remote states)
	isEligible: boolean;
	// Dialog state injected by consumer components
	isDialogOpen: boolean;
	// Local state update in case users dismiss the experience and want to close the spotlight
	isDismissed: boolean;
	setDismissed: (arg: boolean) => void;
	// Updates the shared and remote states and mark the user as seen the experiment
	markAsSeen: () => Promise<void>;
}

const ChangeboardingContext = createContext<ChangeboardingContextValue | undefined>(undefined);

const MigrationChangeboardingProviderInner = ({
	children,
	contentId,
	isDialogOpen,
}: {
	children: React.ReactNode;
	contentId: string;
	isDialogOpen: boolean;
}) => {
	const coordinationClient = useCoordinationClient();
	const [isDismissed, setDismissed] = useState(false);

	const hasSeenLocal = localStorage.getItemAsBoolean(LOCALSTORAGE_KEY);

	const markAsSeenLocal = () => {
		localStorage.setItem(LOCALSTORAGE_KEY, true);
	};

	const [hasPlatformAllowed, markAsSeenPlatform] = useCoordination(
		coordinationClient,
		ENGAGEMENT_ID,
	);

	const markAsSeen = useCallback(async () => {
		markAsSeenLocal();
		await markAsSeenPlatform();
	}, [markAsSeenPlatform]);

	// Content requirements which are not handled by EP:
	// 1- Content should be created/edited by me
	// 2- Content should have more than 10 viewers
	const contentQueryResponse = useQuery(
		gql`
			query MigrationChangeboardingAssetsContentQuery($contentId: ID!) {
				contentAnalyticsViewers(contentId: $contentId) {
					count
				}
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
			}
		`,
		{
			variables: { contentId },
			skip:
				hasSeenLocal ||
				!hasPlatformAllowed ||
				// If the kill-switch FG is off, we shouldn't be reaching this line. This additional FG check is for extra safety.
				!fg('confluence_fe_analytics_migration_changeboarding'),
		},
	);
	const viewersCount = contentQueryResponse.data?.contentAnalyticsViewers?.count || 0;
	const hasEnoughViewers = viewersCount >= MIN_VIEWERS_COUNT_REQUIREMENT;
	const isCurrentUserContributor =
		contentQueryResponse.data?.contentContributors?.isCurrentUserContributor || false;

	const isEligible =
		!isDismissed &&
		!hasSeenLocal &&
		hasPlatformAllowed &&
		hasEnoughViewers &&
		isCurrentUserContributor &&
		// If the kill-switch FG is off, we shouldn't be reaching this line. This additional FG check is for extra safety.
		fg('confluence_fe_analytics_migration_changeboarding');

	const contextValue = useMemo(
		() => ({ isEligible, markAsSeen, isDialogOpen, isDismissed, setDismissed }),
		[isEligible, markAsSeen, isDialogOpen, isDismissed, setDismissed],
	);

	return (
		<ChangeboardingContext.Provider value={contextValue}>{children}</ChangeboardingContext.Provider>
	);
};

const useChangeboardingCoordination = () => {
	const context = useContext(ChangeboardingContext);
	if (!context) {
		throw new Error('useChangeboardingCoordination must be used within a ChangeboardingProvider');
	}
	return context;
};

const spotlightPulseContainerStyles = xcss({
	display: 'inline-block',
});

const MigrationChangeboardingBylinePulseInner = ({ children }: { children: React.ReactNode }) => {
	const { isEligible, isDialogOpen } = useChangeboardingCoordination();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (isEligible) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					action: 'viewed',
					name: GASV3_EVENT_NAME,
				},
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!isEligible) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<Box xcss={spotlightPulseContainerStyles}>
			<SpotlightPulse radius={3} pulse={!isDialogOpen}>
				<Box paddingInline="space.050">{children}</Box>
			</SpotlightPulse>
		</Box>
	);
};

const MigrationChangeboardingGoToPagePulseInner = ({
	children,
	onCTAClick,
	isDialogLoading,
}: {
	children: React.ReactNode;
	onCTAClick: () => void;
	isDialogLoading: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { isEligible, markAsSeen, setDismissed } = useChangeboardingCoordination();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!isEligible) {
		return <Fragment>{children}</Fragment>;
	}

	const handleDismiss = async (subjectId: string) => {
		await markAsSeen();
		setDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: subjectId,
				source: GASV3_EVENT_NAME,
			},
		}).fire();
	};

	const handleProceed = async (subjectId: string) => {
		await markAsSeen();
		onCTAClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: subjectId,
				source: GASV3_EVENT_NAME,
			},
		}).fire();
	};

	return (
		<SpotlightManager
			blanketIsTinted={false}
			onBlanketClicked={async () => {
				await handleDismiss('blanket');
			}}
		>
			<SpotlightTarget name={TARGET_ON_DOM}>{children}</SpotlightTarget>
			<SpotlightTransition>
				<Spotlight
					actions={[
						{
							text: formatMessage(i18n.ctaButton),
							onClick: async () => {
								await handleProceed('cta-button');
							},
						},
						{
							text: formatMessage(i18n.cancelButton),
							appearance: 'subtle',
							onClick: async () => {
								await handleDismiss('cancel-button');
							},
						},
					]}
					heading={formatMessage(i18n.title)}
					// When the dialog content updates, its height changes, but the spotlight cannot update itself (even with shouldWatchTarget enabled).
					// Including "isDialogLoading" in the key forces a rerender of the spotlight once loading is complete.
					key={`${TARGET_ON_DOM}-${isDialogLoading}-key`}
					target={TARGET_ON_DOM}
					targetRadius={3}
					targetOnClick={async () => {
						await handleProceed('target-button');
					}}
					dialogPlacement="right bottom"
					pulse
					shouldWatchTarget
				>
					<FormattedMessage {...i18n.content} />
				</Spotlight>
			</SpotlightTransition>
		</SpotlightManager>
	);
};

const KillSwitchComponent = ({
	fallbackUI,
	children,
}: {
	fallbackUI: React.ReactNode;
	children: React.ReactNode;
}) => {
	// Return early if the FG is off or we're in SSR mode, to avoid server calls
	if (!fg('confluence_fe_analytics_migration_changeboarding') || Boolean(process.env.REACT_SSR)) {
		return <Fragment>{fallbackUI}</Fragment>;
	}

	return <Fragment>{children}</Fragment>;
};

/*
 * PUBLIC COMPONENTS
 * which are wrapped with kill-switch logic
 */
export const MigrationChangeboardingProvider = ({
	children,
	contentId,
	isDialogOpen,
}: {
	children: React.ReactNode;
	contentId: string;
	isDialogOpen: boolean;
}) => {
	return (
		<KillSwitchComponent fallbackUI={children}>
			<MigrationChangeboardingProviderInner contentId={contentId} isDialogOpen={isDialogOpen}>
				{children}
			</MigrationChangeboardingProviderInner>
		</KillSwitchComponent>
	);
};

export const MigrationChangeboardingGoToPagePulse = ({
	children,
	onCTAClick,
	isDialogLoading,
}: {
	children: React.ReactNode;
	onCTAClick: () => void;
	isDialogLoading: boolean;
}) => {
	return (
		<KillSwitchComponent fallbackUI={children}>
			<MigrationChangeboardingGoToPagePulseInner
				onCTAClick={onCTAClick}
				isDialogLoading={isDialogLoading}
			>
				{children}
			</MigrationChangeboardingGoToPagePulseInner>
		</KillSwitchComponent>
	);
};

export const MigrationChangeboardingBylinePulse = ({ children }: { children: React.ReactNode }) => {
	return (
		<KillSwitchComponent fallbackUI={children}>
			<MigrationChangeboardingBylinePulseInner>{children}</MigrationChangeboardingBylinePulseInner>
		</KillSwitchComponent>
	);
};
